import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { postStatus } from '../../lib/changelog.js';
import { css, html } from '../../lib/lit.js';

// Bootstrap badge class per post status.
const STATUS_BADGE = {
    draft: 'text-bg-warning',
    published: 'text-bg-success',
};

function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

// The changelog, drafts included. Posts live in d3-community and render on the
// community portal; this lists them through the Staff API (which forwards the
// staff member's own token) and links each to the editor.
class ChangelogView extends BaseComponent {
    static properties = {
        /** @type {Array<object>} */
        _posts: { state: true },
        /** @type {Map<string, string>} product slug → display name */
        _productNames: { state: true },
        _loading: { state: true },
        _error: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 60rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        table {
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this._posts = [];
        this._productNames = new Map();
        this._loading = true;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const [postsBody, productsBody] = await Promise.all([
                api.get('/changelog/posts'),
                api.get('/changelog/products'),
            ]);
            this._posts = postsBody.posts;
            this._productNames = new Map(productsBody.products.map((p) => [p.slug, p.name]));
        } catch (error) {
            this._error = error.message;
        } finally {
            this._loading = false;
        }
    }

    render() {
        return html`
            <div class="wrap">
                <div class="d-flex align-items-center gap-2 mb-4">
                    <h1 class="h4 mb-0">Changelog</h1>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        type="button"
                        ?disabled=${this._loading}
                        @click=${this._load}
                    >
                        Refresh
                    </button>
                    <a class="btn btn-sm btn-primary ms-auto" href="/changelog/new">
                        <i class="bi bi-plus-lg"></i> New post
                    </a>
                </div>
                <p class="text-muted">
                    Posts shown on the community portal. Drafts are visible only here until published.
                </p>
                ${this._renderBody()}
            </div>
        `;
    }

    _renderBody() {
        if (this._error) {
            return html`<div class="alert alert-danger" role="alert">${this._error}</div>`;
        }
        if (this._loading && this._posts.length === 0) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._posts.length === 0) {
            return html`<p class="text-muted">No posts yet.</p>`;
        }
        return html`
            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Product</th>
                            <th>Status</th>
                            <th>Published</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._posts.map((post) => {
                            const status = postStatus(post);
                            return html`
                                <tr>
                                    <td><a href="/changelog/${post.id}">${post.title}</a></td>
                                    <td>${this._productNames.get(post.product) ?? post.product}</td>
                                    <td><span class="badge ${STATUS_BADGE[status]}">${status}</span></td>
                                    <td class="text-muted">${formatDate(post.publishedAt)}</td>
                                </tr>
                            `;
                        })}
                    </tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('d3-changelog-view', ChangelogView);
